import { ReactNode, useMemo } from 'react';
import { ReactFlowProvider } from '@xyflow/react';

import { WorkflowContext } from '../contexts/WorkflowContext';
import { NodeConfig, WorkflowContextValue } from '../types/node-config';
import { buildReactFlowNodeTypes } from '../utils/node-factory';

type WorkflowProviderProps = {
  children: ReactNode;
  nodeConfigs?: Record<string, NodeConfig>;
};

/**
 * Provides workflow node configurations to all child components.
 * Wraps ReactFlowProvider and adds custom node registry context.
 *
 * @example
 * ```tsx
 * import { WorkflowProvider } from './providers/WorkflowProvider';
 * import { nodeConfigs } from './nodes/configs';
 *
 * <WorkflowProvider nodeConfigs={nodeConfigs}>
 *   <WorkflowBuilder />
 * </WorkflowProvider>
 * ```
 */
export function WorkflowProvider({ children, nodeConfigs = {} }: WorkflowProviderProps) {
  const nodeTypes = useMemo(() => buildReactFlowNodeTypes(nodeConfigs), [nodeConfigs]);

  const contextValue: WorkflowContextValue = useMemo(
    () => ({
      nodeConfigs,
      getNodeConfig: (type: string) => nodeConfigs[type],
      getAllNodeConfigs: () => nodeConfigs,
      nodeTypes,
    }),
    [nodeConfigs, nodeTypes]
  );

  return (
    <WorkflowContext.Provider value={contextValue}>
      <ReactFlowProvider>{children}</ReactFlowProvider>
    </WorkflowContext.Provider>
  );
}
